import React from 'react';

import Delete from 'Components/Partials/Modals/Delete';
import AdminSendEmailModal from 'Components/Partials/Modals/AdminSendEmailModal';
import {IndexTable} from 'Components/Utilities';
import {BooleanFilter, DateTimeFilter, AsyncSelectFilter} from 'Components/Filters';

import AdminLayout from 'Pages/Admin/AdminLayout';

import AdminUsersApi from 'Services/Api/Admin/Users';
import GroupsApi from 'Services/Api/Groups/Groups';
import Confirm from "Components/Partials/Modals/Confirm";

export default  class UsersIndex extends React.Component {
    /**
     * @var columns
     * @type {array}
     */
    columns = [
        {
            label: 'First Name',
            value: 'first_name',
            type: 'string',
            orderable: 'first_name'
        },
        {
            label: 'Last Name',
            value: 'last_name',
            type: 'string',
            orderable: 'last_name'
        },
        {
            label: 'Email',
            value: 'email',
            type: 'string',
            orderable: 'email'
        },
        {
            label: 'Subscription',
            value: 'active_cashier_subscription_name',
            type: 'string',
            orderable: null,
            feature: 'subscriptions'
        },
        {
            label: 'Number of Invites',
            value: 'number_of_invites',
            type: 'string',
            orderable: 'number_of_invites',
            feature: 'user_can_invite',
            hideByDefault: true,
        },
        {
            label: 'Number Invited',
            value: 'number_invited',
            type: 'string',
            orderable: null,
            feature: 'user_can_invite',
            hideByDefault: true,
        },
        {
            label: 'Group',
            value: (record) => record.groups?.map((group, i) => (
                <>
                    {group.name}{i !== (record.groups.length - 1) ? ', ' : null}
                </>
            )),
            type: 'callback',
            orderable: null,
            feature: 'groups',
            filters: [
                {
                    column: 'group_ids',
                    label: 'Group',
                    component: AsyncSelectFilter,
                    props: {
                        searchCallback: (data) => GroupsApi.get(null, {
                            ...data,
                            hasNoTeam: true
                        }),
                        searchLabelKey: "name"
                   },
                    defaultMethod: false
                }
            ]
        },
        {
            label: 'Admin',
            value: 'is_admin',
            type: 'boolean',
            orderable: 'is_admin',
            filters: [
                {
                    column: 'is_admin',
                    label: 'Admin',
                    component: BooleanFilter
                }
            ]
        },
        {
            label: 'Enabled',
            value: 'enabled',
            type: 'boolean',
            orderable: 'enabled',
            filters: [
                {
                    column: 'enabled',
                    label: 'Enabled',
                    component: BooleanFilter
                }
            ],
            hideByDefault: true
        },
        {
            label: 'Created At',
            value: 'created_at',
            type: 'datetime',
            orderable: 'created_at',
            hideByDefault: true,
            filters: [
                {
                    column: 'created_at',
                    label: 'Created At Before',
                    component: DateTimeFilter,
                    props: {
                        type: 'datetime',
                        direction: 'before'
                    }
                },
                {
                    column: 'created_at',
                    label: 'Created At After',
                    component: DateTimeFilter,
                    props: {
                        type: 'datetime',
                        direction: 'after'
                    }
                }
            ]
        }
    ];

    /**
     * @var state
     */
    state = {
        hardPassword: null,
    };

    /**
     * @method componentDidMount
     */
    componentDidMount() {
        {window.base.using_hard_password &&
            this.fetchHardPassword();
        }
    }

    /**
     * @method fetchHardPassword
     */
    fetchHardPassword = async () => {
        const response = await AdminUsersApi.getHardPassword();

        this.setState({
            hardPassword: response?.data?.hard_password,
        });
    }

    /**
     * @method getRowActions
     * @param {object} item
     */
    getRowActions = (item) => {
        let buttons = [
            {
                type: "link",
                to: "/admin/users/show/"+item.id,
                text: "View"
            },
            {
                type: "link",
                to: "/admin/users/store/"+item.id,
                text: "Update"
            }
        ];

        if (window.base.features.allow_profile_change) {
            buttons.push({
                type: "link",
                to: "/admin/users/profile/"+item.id,
                text: "Profile"
            });
        }

        if (item.is_admin || !window.base.using_hard_password) {
            buttons.push({
                type: "link",
                to: "/admin/users/update-password/"+item.id,
                text: "Reset Password"
            });

            buttons.push({
                type: "modalTrigger",
                component: AdminSendEmailModal,
                props: {
                    user: item,
                    onSubmit: AdminUsersApi.sendPasswordResetLink,
                    emailTitle: 'password reset link'
                },
                text: 'Send Password Reset Link'
            });
        }

        buttons.push({
            type: "modalTrigger",
            component: AdminSendEmailModal,
            props: {
                user: item,
                onSubmit: AdminUsersApi.sendUserInvite,
                emailTitle: 'user invite',
                userInvite: true
            },
            text: 'Send User Invite'
        });

        buttons.push({
            type: "modalTrigger",
            component: Confirm,
            props: {
                message: `Are you sure you want to send an email attendance confirmation to this user?`,
                // Confirm component expects `onConfirm` to be a function.
                // Call the API with the current user's id when confirmed.
                onConfirm: () => AdminUsersApi.sendConfirmation(item.id),
            },
            text: 'Send Email Confirmation'
        });

        if (window.base.features.notifications) {
            buttons.push({
                type: "link",
                to: "/admin/user-comms/"+item.id,
                text: 'Comms'
            });
        }

        if (window.base.features.subscriptions || window.base.features.products) {
            buttons.push({
                type: "link",
                to: "/admin/cashier/user-billings/"+item.id,
                text: 'Cashier'
            });
        }

        buttons.push({
            type: "modalTrigger",
            component: Delete,
            props: {
                message: `Are you sure you want to delete this account?
                    Once an account is deleted, all of its resources and data will be permanently deleted.
                    This action can not be reversed.`,
                itemId: item.id,
                onDeleteApi: AdminUsersApi.delete
            },
            text: 'Delete'
        });

        return buttons;
    }

    /**
     * @method render
     * @return {JSX.Element}
     */
    render() {
        const {hardPassword} = this.state;

        let title = 'Users';

        if (window.base.using_hard_password) {
            title += ' (Hard password in use: ' + hardPassword + ')';
        }

        return (
            <AdminLayout title={title}>
                <IndexTable
                    columns={this.columns}
                    defaultOrder={{
                        order_by: 'first_name',
                        order: 'asc'
                    }}
                    displaySearchBar={true}
                    storeLink="/admin/users/store"
                    loadDataCallback={(data) => AdminUsersApi.get(null, data)}
                    rowActions={this.getRowActions}
                    rowClickRedirect={(item) => "/admin/users/store/"+item.id}
                    eventApi={AdminUsersApi}
                    report="Users"
                />
            </AdminLayout>
        )
    }
}
